import assignNodeData from 'tree-node-data';
import {
	addLessonEntity,
	addTopicEntity,
	addQuizEntity,
	updateLessons,
	updateSectionHeadings,
	updateQuestions,
	addQuestionEntity,
	setActiveLesson,
	setDraggableEntity,
	setIsDropDisabledLesson,
	addFinalQuizEntity,
	sortFinalQuizzesOutline,
	removeEntity,
	removeExistingEntity,
} from '~/redux/actions/workspaceActions';
import { store } from '~/redux/configureStore';
import { treeNodeUtils, treeConfig } from '~/util';
import { updateAnswer, addSectionHeading } from '~/api';

/**
 * A little function to help us with reordering the result. Used with React Beautiful DnD to manage the data array and trees
 *
 * @param {*} list
 * @param {*} startIndex
 * @param {*} endIndex
 */
export const reorder = ( list, startIndex, endIndex ) => {
	const result = Array.from( list );
	const [ removed ] = result.splice( startIndex, 1 );
	result.splice( endIndex, 0, removed );

	return result.filter( ( element ) => element !== undefined );
};

/**
 * Helper to update the section headings order key in the lessons outline
 * The section heading order value should match its index in the lessons outline
 *
 * @param {*} lessons
 */
export const updateSectionHeadingsOrder = ( lessons ) => {
	return lessons.map( ( node, index ) => {
		if ( 'section-heading' === node.type ) {
			return { ...node, order: index };
		}
		return node;
	} );
};

/**
 * Helper to get the updated section headings array from the lessons outline
 *
 * @param {*} lessons
 */
export const getSectionHeadings = ( lessons ) => {
	return lessons.filter( ( node ) => 'section-heading' === node.type );
};

/**
 * Fires when we start dragging a node
 * This is used to compare the dragged node to the droppable area
 *
 * @param {Object} result
 */
export const onDragStartEvent = ( result ) => {
	const { draggableId } = result;

	// The whole node is stored in the ID
	const draggedNode = JSON.parse( draggableId );

	store.dispatch(
		setDraggableEntity( {
			currentDraggableEntity: draggedNode,
			currentDroppableEntity: null,
		} )
	);
};

/**
 * Fired when we finish dragging a node in the course builder
 * This contains the logic to update the workspace, but also checks to see if the dropped object can be dropped.
 * For example, we can't drop quizzes before topics in a lesson
 * Top level elements can only be lessons (course builder), quizzes (final quizzes area)
 *
 * @param {Object} result
 */
export const onDragEndEvent = ( result ) => {
	const { source, destination, draggableId } = result;

	if ( ! destination ) {
		return false;
	}

	// since we are storing extra data in the id in JSON, parse the data.
	const draggableIdObject = JSON.parse( draggableId );
	const sourceDroppableIdObject = JSON.parse( source.droppableId );
	const destinationDroppableIdObject = JSON.parse( destination.droppableId );

	// Get the store.
	const storeState = store.getState();

	// Tree utils
	const workspaceNodes = Object.values(
		storeState.workspace.present.lessons
	);
	const workspaceNodesWithData = assignNodeData( workspaceNodes, treeConfig );

	// Sorting.
	if (
		sourceDroppableIdObject.ID === destinationDroppableIdObject.ID &&
		source.index !== destination.index
	) {
		// If we're dropping lessons
		if (
			'lesson' ===
				storeState.workspace.present.currentDraggableEntity.type ||
			'sfwd-lessons' ===
				storeState.workspace.present.currentDraggableEntity.type ||
			'section-heading' ===
				storeState.workspace.present.currentDraggableEntity.type
		) {
			const newState = Object.values(
				storeState.workspace.present.lessons
			);

			const lessons = reorder(
				newState,
				source.index,
				destination.index
			);

			// Update section heading order in outline (if any section heading)
			const newLessons = updateSectionHeadingsOrder( lessons );

			// Get array of sections
			const newSections = getSectionHeadings( newLessons );

			// Update lessons in workspace
			store.dispatch( updateLessons( newLessons ) );

			// Update sections in workspace
			store.dispatch( updateSectionHeadings( newSections ) );

			// Update sections post meta
			addSectionHeading(
				LearnDashData.post_data.builder_post_id,
				newSections
			);

			if (
				'section-heading' !==
				storeState.workspace.present.currentDraggableEntity.type
			) {
				// Set the active lesson to the current dropped lesson
				store.dispatch(
					setActiveLesson( {
						activeLesson: draggableIdObject.ID,
					} )
				);
			}
		}
	}

	// Differentiate between when we need to sort within the workspace or when we are adding new items.
	if (
		'course-workspace-droppable' === destinationDroppableIdObject.ID &&
		'droppable-lesson' === sourceDroppableIdObject.ID
	) {
		if (
			( 'lesson' ===
				storeState.workspace.present.currentDraggableEntity.type ||
				'sfwd-lessons' ===
					storeState.workspace.present.currentDraggableEntity
						.type ) &&
			draggableIdObject.ID
		) {
			// get lesson from raw data.
			const draggableLesson = storeState.data.lessons.find(
				( lesson ) => lesson.ID === draggableIdObject.ID
			);

			// Add index to the draggable item when dropped
			draggableLesson.index = destination.index;

			// Fire action now
			store.dispatch( addLessonEntity( draggableLesson ) );
		}
	}

	// We are dropping topics
	if (
		'topic' === draggableIdObject.type ||
		'sfwd-topic' === draggableIdObject.type
	) {
		// get node on which node was dropped
		const parentNode = treeNodeUtils.getNodeByKey(
			workspaceNodes,
			parseInt( destinationDroppableIdObject.ID )
		);
		const parentNodeWithData = treeNodeUtils.getNodeByKey(
			workspaceNodesWithData,
			parseInt( destinationDroppableIdObject.ID )
		);

		if ( parentNode ) {
			// Find the entity dropped now.
			const droppedNode = draggableIdObject;

			// If we have dropped node data.
			if ( droppedNode ) {
				// We are not re ordering the topics, so add new one.
				if (
					sourceDroppableIdObject.ID !==
					destinationDroppableIdObject.ID
				) {
					if ( 'droppable-topic' !== sourceDroppableIdObject.ID ) {
						// Remove entity from workspace, which will be added later on.
						store.dispatch(
							removeEntity( {
								ID: parseInt( draggableIdObject.ID ),
							} )
						);
					}

					// Add the new entity
					store.dispatch(
						addTopicEntity(
							parseInt( destinationDroppableIdObject.ID ),
							destination.index,
							droppedNode
						)
					);
				} else if (
					sourceDroppableIdObject.ID ===
						destinationDroppableIdObject.ID &&
					source.index !== destination.index
				) {
					// Sorting between topics
					if (
						parentNode.tree[ source.index ].type ===
						parentNode.tree[ destination.index ].type
					) {
						parentNode.tree = reorder(
							parentNode.tree,
							source.index,
							destination.index
						);

						store.dispatch( updateLessons( workspaceNodes ) );
					}
				}

				// If we are dropping on a parent node, set active lesson to the parent node.
				if (
					parentNodeWithData.ID &&
					! parentNodeWithData.nodeData.parent
				) {
					store.dispatch(
						setActiveLesson( {
							activeLesson: parentNodeWithData.ID,
						} )
					);
				}
			}
		}
	}

	// Dropping quiz in the final quizzes section
	if (
		( 'quiz' === draggableIdObject.type ||
			'sfwd-quiz' === draggableIdObject.type ) &&
		'course-workspace-quiz-droppable' === destinationDroppableIdObject.ID
	) {
		if (
			( 'quiz' ===
				storeState.workspace.present.currentDraggableEntity.type ||
				'sfwd-quiz' ===
					storeState.workspace.present.currentDraggableEntity
						.type ) &&
			draggableIdObject.ID
		) {
			if (
				sourceDroppableIdObject.ID ===
					destinationDroppableIdObject.ID &&
				source.index !== destination.index
			) {
				// re ordering within the final quizzes
				const newState = Object.values(
					storeState.workspace.present.quizzes
				);

				const quizzes = reorder(
					newState,
					source.index,
					destination.index
				);

				store.dispatch( sortFinalQuizzesOutline( quizzes ) );
			} else {
				// check if we are dropping from the sidebar
				if ( 'droppable-quiz' !== sourceDroppableIdObject.ID ) {
					// we are moving quiz from one parent to another, most probably from a lesson quiz to final quiz
					store.dispatch(
						removeEntity( {
							ID: draggableIdObject.ID,
						} )
					);
				}

				// Fire action now
				store.dispatch(
					addFinalQuizEntity( destination.index, draggableIdObject )
				);
			}
		}
	}
	// Drop quiz within the lessons area
	else if (
		'quiz' === draggableIdObject.type ||
		'sfwd-quiz' === draggableIdObject.type
	) {
		// get node on which node was dropped
		const parentNode = treeNodeUtils.getNodeByKey(
			workspaceNodes,
			parseInt( destinationDroppableIdObject.ID )
		);
		const parentNodeWithData = treeNodeUtils.getNodeByKey(
			workspaceNodesWithData,
			parseInt( destinationDroppableIdObject.ID )
		);

		if ( parentNode ) {
			// Get the dropped node details
			const droppedNode = draggableIdObject;

			if ( droppedNode ) {
				if (
					sourceDroppableIdObject.ID ===
						destinationDroppableIdObject.ID &&
					source.index !== destination.index
				) {
					// if we are trying to drop before a quiz, find the first quiz index and insert at that index
					const firstQuizIndex = parentNodeWithData.tree.findIndex(
						( el ) => {
							return (
								'quiz' === el.type || 'sfwd-quiz' === el.type
							);
						}
					);

					if ( -1 !== firstQuizIndex ) {
						// bail out if trying to drop before a topic
						if ( destination.index < firstQuizIndex - 1 ) {
							return false;
						}
					}

					parentNode.tree = reorder(
						parentNode.tree,
						source.index,
						destination.index
					);

					store.dispatch( updateLessons( workspaceNodes ) );
				} else {
					// Check if we moved a quiz from the final quizzes section
					if (
						'course-workspace-quiz-droppable' ===
						sourceDroppableIdObject.ID
					) {
						store.dispatch(
							removeEntity( {
								ID: draggableIdObject.ID,
							} )
						);
					}

					// If we are not dropping on a lesson
					if (
						'lesson' !== destinationDroppableIdObject.type ||
						'sfwd-lessons' !== destinationDroppableIdObject.type
					) {
						if (
							sourceDroppableIdObject.ID !==
							destinationDroppableIdObject.ID
						) {
							store.dispatch(
								removeExistingEntity( {
									ID: draggableIdObject.ID,
								} )
							);

							// Adding a new node.
							store.dispatch(
								addQuizEntity(
									parseInt( destinationDroppableIdObject.ID ),
									destination.index,
									droppedNode
								)
							);
						}
					} else {
						// check previous sibling
						let topicDestinationIndex = destination.index;
						if ( 0 < destination.index ) {
							if (
								'topic' ===
									parentNodeWithData.tree[
										destination.index - 1
									].type ||
								'topic' ===
									parentNodeWithData.tree[ destination.index ]
										.type ||
								'sfwd-topic' ===
									parentNodeWithData.tree[
										destination.index - 1
									].type ||
								'sfwd-topic' ===
									parentNodeWithData.tree[ destination.index ]
										.type
							) {
								// if we are trying to drop before a quiz, find the first quiz index and insert topic at that index
								const firstQuizIndex = parentNodeWithData.tree.findIndex(
									( el ) => {
										return (
											'quiz' === el.type ||
											'sfwd-quiz' === el.type
										);
									}
								);

								if ( -1 !== firstQuizIndex ) {
									topicDestinationIndex = firstQuizIndex;
								} else {
									topicDestinationIndex =
										parentNodeWithData.tree.length;
								}
							}
						} else if (
							0 === destination.index &&
							parentNodeWithData.tree[ destination.index ] &&
							( 'topic' ===
								parentNodeWithData.tree[ destination.index ]
									.type ||
								'sfwd-topic' ===
									parentNodeWithData.tree[ destination.index ]
										.type )
						) {
							topicDestinationIndex = 1;
						}

						// add node now
						store.dispatch(
							addQuizEntity(
								parseInt( destinationDroppableIdObject.ID ),
								topicDestinationIndex,
								droppedNode
							)
						);
					}
				}

				// Set the top most parent to be the active lesson
				let activeLesson = parentNode.ID;
				if (
					'lesson' !== parentNode.type &&
					'sfwd-lessons' !== parentNode.type
				) {
					activeLesson = parentNodeWithData.nodeData.ancestors[ 0 ];
				}

				store.dispatch( setActiveLesson( { activeLesson } ) );
			}
		}
	}

	store.dispatch(
		setIsDropDisabledLesson( {
			isDropDisabledLesson: null,
		} )
	);

	store.dispatch(
		setDraggableEntity( {
			currentDraggableEntity: null,
		} )
	);
};

/**
 * Fired when there is an update to the dragged node
 *
 * @param {Object} result
 */
export const onDragUpdateEvent = ( result ) => {
	const { draggableId, destination } = result;

	if ( ! destination ) {
		return false;
	}

	const draggableIdObject = JSON.parse( draggableId );
	const destinationDroppableIdObject = JSON.parse( destination.droppableId );
	const destinationIndex = destination.index;

	// Get the store.
	const storeState = store.getState();

	// Map of answer type
	const answerTypeMap = LearnDashData.questions_types_map;

	let workspaceNodes = [];
	if ( 'sfwd-quiz' !== LearnDashData.post_data.builder_post_type ) {
		workspaceNodes = Object.values( storeState.workspace.present.lessons );
	} else {
		workspaceNodes = Object.values(
			storeState.workspace.present.questions
		);
	}
	const workspaceNodesWithData = assignNodeData( workspaceNodes, treeConfig );

	if (
		( 'quiz' === draggableIdObject.type ||
			'sfwd-quiz' === draggableIdObject.type ) &&
		( 'lesson' === destinationDroppableIdObject.type ||
			'sfwd-lessons' === destinationDroppableIdObject.type )
	) {
		const destinationDroppableWithData = treeNodeUtils.getNodeByKey(
			workspaceNodesWithData,
			parseInt( destinationDroppableIdObject.ID )
		);

		if (
			destinationDroppableWithData.tree[ destinationIndex ] &&
			( 'topic' ===
				destinationDroppableWithData.tree[ destinationIndex ].type ||
				'sfwd-topic' ===
					destinationDroppableWithData.tree[ destinationIndex ].type )
		) {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: destinationDroppableWithData.ID,
				} )
			);
		} else {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: null,
				} )
			);
		}
	}

	if (
		'topic' === draggableIdObject.type ||
		'sfwd-topic' === draggableIdObject.type
	) {
		const destinationDroppableWithData = treeNodeUtils.getNodeByKey(
			workspaceNodesWithData,
			parseInt( destinationDroppableIdObject.ID )
		);

		if (
			destinationDroppableWithData.tree[ destinationIndex ] &&
			( 'quiz' ===
				destinationDroppableWithData.tree[ destinationIndex ].type ||
				'sfwd-quiz' ===
					destinationDroppableWithData.tree[ destinationIndex ].type )
		) {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: destinationDroppableWithData.ID,
				} )
			);
		} else {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: null,
				} )
			);
		}
	}

	if ( 'answer' === draggableIdObject.type ) {
		const destinationDroppableWithData = treeNodeUtils.getNodeByKey(
			workspaceNodesWithData,
			parseInt( destinationDroppableIdObject.ID )
		);
		if (
			destinationDroppableWithData.answers[
				answerTypeMap[ destinationDroppableWithData.question_type ]
			][ destinationIndex ] &&
			'answer' ===
				destinationDroppableWithData.answers[
					answerTypeMap[ destinationDroppableWithData.question_type ]
				][ destinationIndex ].type
		) {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: destinationDroppableWithData.ID,
				} )
			);
		} else {
			store.dispatch(
				setIsDropDisabledLesson( {
					isDropDisabledLesson: null,
				} )
			);
		}
	}

	return false;
};

/**
 * Fired when we finish dragging a node on the quiz edit page
 * This contains the logic to update the workspace, but also checks to see if the dropped object can be dropped.
 * Top level elements can only be questions
 *
 * @param {Object} result
 */
export const onDragEndEventQuiz = async ( result ) => {
	const { source, destination, draggableId } = result;

	if ( ! destination ) {
		return false;
	}

	// since we are storing extra data in the id in JSON, parse the data.
	const draggableIdObject = JSON.parse( draggableId );
	const sourceDroppableIdObject = JSON.parse( source.droppableId );
	const destinationDroppableIdObject = JSON.parse( destination.droppableId );

	// Get the store.
	const storeState = store.getState();

	// Map of answer type
	const answerTypeMap = LearnDashData.questions_types_map;

	// Sorting.
	if (
		sourceDroppableIdObject.ID === destinationDroppableIdObject.ID &&
		source.index !== destination.index
	) {
		// We are dropping a question
		if (
			'question' ===
				storeState.workspace.present.currentDraggableEntity.type ||
			'sfwd-question' ===
				storeState.workspace.present.currentDraggableEntity.type
		) {
			const newState = Object.values(
				storeState.workspace.present.questions
			);

			const questions = reorder(
				newState,
				source.index,
				destination.index
			);

			store.dispatch( updateQuestions( questions ) );

			// Set the active lesson
			store.dispatch(
				setActiveLesson( {
					activeLesson: draggableIdObject.ID,
				} )
			);
		} else if (
			'answer' ===
			storeState.workspace.present.currentDraggableEntity.type
		) {
			// We're dropping an answer
			// Tree utils
			const workspaceNodes = Object.values(
				storeState.workspace.present.questions
			);
			const workspaceNodesWithData = assignNodeData(
				workspaceNodes,
				treeConfig
			);

			// get node on which node was dropped
			const parentNode = treeNodeUtils.getNodeByKey(
				workspaceNodes,
				parseInt( destinationDroppableIdObject.ID )
			);
			const parentNodeWithData = treeNodeUtils.getNodeByKey(
				workspaceNodesWithData,
				parseInt( destinationDroppableIdObject.ID )
			);

			if ( parentNode ) {
				// Find the entity dropped now.
				const droppedNode = draggableIdObject;

				// If we have dropped node data.
				if ( droppedNode ) {
					if (
						sourceDroppableIdObject.ID ===
							destinationDroppableIdObject.ID &&
						source.index !== destination.index
					) {
						if (
							parentNode.answers[
								answerTypeMap[ parentNode.question_type ]
							][ source.index ].type ===
							parentNode.answers[
								answerTypeMap[ parentNode.question_type ]
							][ destination.index ].type
						) {
							// Sorting between answers
							parentNode.answers[
								answerTypeMap[ parentNode.question_type ]
							] = reorder(
								parentNode.answers[
									answerTypeMap[ parentNode.question_type ]
								],
								source.index,
								destination.index
							);

							// Update order in our workspace
							store.dispatch( updateQuestions( workspaceNodes ) );

							// Update question in the background at the same time
							await updateAnswer(
								parentNode.ID,
								parentNode.answers[
									answerTypeMap[ parentNode.question_type ]
								]
							);
						}
					}

					// If we are dropping on a parent node.
					if (
						parentNodeWithData.ID &&
						! parentNodeWithData.nodeData.parent
					) {
						store.dispatch(
							setActiveLesson( {
								activeLesson: parentNodeWithData.ID,
							} )
						);
					}
				}
			}
		}
	}

	// Differentiate between when we need to sort within the workspace or when we are adding new items.
	if (
		'quiz-workspace-droppable' === destinationDroppableIdObject.ID &&
		'droppable-question' === sourceDroppableIdObject.ID
	) {
		if (
			( 'question' ===
				storeState.workspace.present.currentDraggableEntity.type ||
				'sfwd-question' ===
					storeState.workspace.present.currentDraggableEntity
						.type ) &&
			draggableIdObject.ID
		) {
			// get question from raw data.
			const draggableQuestion = storeState.data.questions.find(
				( question ) => question.ID === draggableIdObject.ID
			);

			// Add index to the draggable item when dropped
			draggableQuestion.index = destination.index;

			// Fire action now
			store.dispatch( addQuestionEntity( draggableQuestion ) );
		}
	}

	store.dispatch(
		setDraggableEntity( {
			currentDraggableEntity: null,
		} )
	);
};
